import {queries} from '@/shared/integrations/zero'
import {getSequenceUseBy, isPreparationExpired} from '@cypro/engine/utils'
import {useQuery, useZero as useZeroClient} from '@rocicorp/zero/react'
import {useCallback, useEffect, useMemo, useState} from 'react'

const TICK_MS = 30_000

/** Use-by of a produced preparation and whether it has passed, re-checked every 30 s; the server enforces the same rule (CY-327). */
export function usePreparationUseBy(sequenceId: string, enabled = true) {
  const [source] = useQuery(queries.patientsTherapies.getPreparationLabelSource(sequenceId), {enabled})
  const [statusLogs] = useQuery(queries.patientsTherapies.getSequenceStatusLogs(sequenceId), {enabled})
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!enabled) return
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [enabled])

  const useBy = useMemo(
    () => (enabled && source ? getSequenceUseBy(source, statusLogs ?? []) : null),
    [enabled, source, statusLogs]
  )

  return {useBy, isExpired: isPreparationExpired(useBy, now)}
}

/** One-off check before releasing or administering, for entry points that do not show the use-by (list rows). */
export function useCheckPreparationExpired() {
  // the app's useZero spreads the client into a plain object, which drops prototype methods like run()
  const zero = useZeroClient()

  return useCallback(
    async (sequenceId: string) => {
      const [source, statusLogs] = await Promise.all([
        zero.run(queries.patientsTherapies.getPreparationLabelSource(sequenceId), {type: 'complete'}),
        zero.run(queries.patientsTherapies.getSequenceStatusLogs(sequenceId), {type: 'complete'}),
      ])
      const useBy = source ? getSequenceUseBy(source, statusLogs) : null
      return isPreparationExpired(useBy, Date.now())
    },
    [zero]
  )
}
